import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../../auth/AuthContext";
import { createTrip, getUserSettings, updateUserSettings } from "../../../api/trips";
import { useChatStore } from "../../../store/chatStore";
import { event as analyticsEvent } from "../../../lib/analytics";
import { composeMessage } from "../../../pages/Landing/composeMessage";
import { buildTripTitle } from "../../../pages/HomePage/pendingTrip";
import { PlaceAutocompleteInput } from "../PlaceAutocompleteInput";
import { Turnstile, useTurnstileToken } from "../Turnstile";
import "./LandingPageForm.less";

function fmtDate(d: Date | null): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

type FieldErrors = { from?: boolean; to?: boolean; dates?: boolean };

type LandingPageFormProps = {
  region?: "westCoast" | "eastCoast";
};

export function LandingPageForm({ region }: LandingPageFormProps) {
  const regionKey = region ?? "generic";
  const { t } = useTranslation("landing");
  const navigate = useNavigate();
  const { user, signInAnonymous } = useAuth();

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [roundTrip, setRoundTrip] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [into, setInto] = useState("");
  const [maxDrive, setMaxDrive] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anonEnabled, setAnonEnabled] = useState(true);

  const { setToken: setTurnstileToken, getToken: getTurnstileToken } = useTurnstileToken();

  const fromSelectedLabel = useRef<string | null>(null);
  const toSelectedLabel = useRef<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isFromValid = () => from.trim() !== "" && fromSelectedLabel.current === from;
  const isToValid = () => roundTrip || (to.trim() !== "" && toSelectedLabel.current === to);

  const validate = (): boolean => {
    const fromOk = isFromValid();
    const toOk = isToValid();
    const datesOk = startDate != null;
    const errors: FieldErrors = {};
    if (!fromOk) errors.from = true;
    if (!toOk) errors.to = true;
    if (!datesOk) errors.dates = true;
    setFieldErrors(errors);
    return fromOk && toOk && datesOk;
  };

  const handleFromChange = useCallback((value: string) => {
    setFrom(value);
    if (fromSelectedLabel.current && value !== fromSelectedLabel.current) {
      fromSelectedLabel.current = null;
    }
  }, []);

  const handleToChange = useCallback((value: string) => {
    setTo(value);
    if (toSelectedLabel.current && value !== toSelectedLabel.current) {
      toSelectedLabel.current = null;
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    getUserSettings()
      .then((s) => {
        if (s.maxDrivingHoursPerDay != null) setMaxDrive(s.maxDrivingHoursPerDay);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetch("/api/registration-status")
      .then((r) => r.json() as Promise<{ anonEnabled?: boolean }>)
      .then((data) => {
        if (data.anonEnabled === false) setAnonEnabled(false);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!validate()) return;

    const depart = fmtDate(startDate);
    const returnDate = fmtDate(endDate);
    const fields = { from, to, roundTrip, depart, returnDate, freeText: into, maxDrivingHours: maxDrive };

    setSubmitting(true);
    setError(null);
    try {
      let activeUser = user;
      if (!activeUser) {
        if (!anonEnabled) {
          navigate("/register");
          return;
        }
        activeUser = await signInAnonymous();
      }

      const title = buildTripTitle(from.trim(), roundTrip ? "" : to.trim(), roundTrip);
      const startLoc = from.trim() || undefined;
      const endLoc = (roundTrip ? from.trim() : to.trim()) || undefined;
      const turnstileToken = getTurnstileToken() || undefined;
      const trip = await createTrip({
        title,
        maxDrivingHoursPerDay: maxDrive,
        ...(startLoc ? { startLocation: startLoc } : {}),
        ...(endLoc ? { endLocation: endLoc } : {}),
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      analyticsEvent("trip_created");
      if (!activeUser.isAnonymous) {
        updateUserSettings({ maxDrivingHoursPerDay: maxDrive }).catch(() => {});
      }
      useChatStore.getState().setPending(trip.id, composeMessage(fields));
      navigate(`/trips/${trip.id}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      if (msg === "ACCOUNT_PAUSED") {
        setError(t("accountPaused"));
      } else if (msg === "ANON_IP_LIMIT" || msg === "ANON_TURN_BUDGET_EXHAUSTED") {
        setError(t("anonRateLimited"));
      } else if (msg === "ANON_DISABLED") {
        setError(t("anonDisabled"));
      } else {
        setError(t("createFailed"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-form-card" data-testid="lp-form">
      <div className="lp-form-row">
        <div className="lp-form-field">
          <label htmlFor="lp-from">{t("from")}</label>
          <PlaceAutocompleteInput
            id="lp-from"
            value={from}
            onChange={handleFromChange}
            onSelect={(s) => {
              fromSelectedLabel.current = s.label;
              setFieldErrors((prev) => ({ ...prev, from: false }));
            }}
            onBlur={() => {
              if (!isFromValid()) setFieldErrors((prev) => ({ ...prev, from: true }));
            }}
            invalid={!!fieldErrors.from}
            placeholder={t(`regions.${regionKey}.fromPlaceholder`)}
          />
          {fieldErrors.from && <p className="lp-form-field__error">{t("errorFrom")}</p>}
        </div>

        <div className="lp-form-field">
          <div className="lp-form-field__label-row">
            <label htmlFor="lp-to">{t("to")}</label>
            <button
              type="button"
              className="lp-cycle"
              data-testid="lp-round-trip"
              aria-pressed={roundTrip}
              onClick={() => {
                if (!roundTrip) setFieldErrors((prev) => ({ ...prev, to: false }));
                setRoundTrip((v) => !v);
              }}
            >
              {t("roundTripPill")}
            </button>
          </div>
          <PlaceAutocompleteInput
            id="lp-to"
            value={roundTrip ? from : to}
            onChange={handleToChange}
            onSelect={(s) => {
              toSelectedLabel.current = s.label;
              setFieldErrors((prev) => ({ ...prev, to: false }));
            }}
            onBlur={() => {
              if (!isToValid()) setFieldErrors((prev) => ({ ...prev, to: true }));
            }}
            invalid={!!fieldErrors.to && !roundTrip}
            disabled={roundTrip}
            placeholder={roundTrip ? t("roundTripPlaceholder") : t(`regions.${regionKey}.toPlaceholder`)}
          />
          {fieldErrors.to && !roundTrip && <p className="lp-form-field__error">{t("errorTo")}</p>}
        </div>

        <div className={`lp-form-field${fieldErrors.dates ? " lp-form-field--invalid" : ""}`} data-testid="lp-dates">
          <label htmlFor="lp-dates">{t("dates")}</label>
          <DatePicker
            id="lp-dates"
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={([start, end]: [Date | null, Date | null]) => {
              setStartDate(start);
              setEndDate(end);
              if (start != null) setFieldErrors((prev) => ({ ...prev, dates: false }));
            }}
            placeholderText={t("datesPlaceholder")}
            dateFormat="MMM d"
            autoComplete="off"
          />
          {fieldErrors.dates && <p className="lp-form-field__error">{t("errorDates")}</p>}
        </div>

        <div className="lp-form-field lp-form-field--stepper">
          <label>{t("maxDriving")}</label>
          <div className="lp-days-stepper">
            <button
              type="button"
              aria-label={t("decreaseDriving")}
              onClick={() => setMaxDrive((h) => Math.max(1, h - 1))}
            >
              −
            </button>
            <span data-testid="lp-max-drive-value">
              {maxDrive}<small>hrs</small>
            </span>
            <button
              type="button"
              aria-label={t("increaseDriving")}
              onClick={() => setMaxDrive((h) => Math.min(12, h + 1))}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="lp-form-into-row">
        <div className="lp-form-into">
          <label htmlFor="lp-into">{t("themes")}</label>
          <input
            id="lp-into"
            type="text"
            value={into}
            onChange={(e) => setInto(e.target.value)}
            placeholder={t(`regions.${regionKey}.themesPlaceholder`)}
          />
        </div>

        <div className="lp-form-submit-row">
          {!user && <Turnstile onToken={setTurnstileToken} />}
          <button
            type="button"
            className="lp-form-submit"
            data-testid="lp-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t("creating") : t("submit")}
          </button>
        </div>
      </div>

      {error && <p className="lp-form-card__error">{error}</p>}
    </div>
  );
}
